import { useMutation } from '@tanstack/react-query'
import { message } from 'antd'
import { jwtDecode } from 'jwt-decode'
import { useNavigate } from 'react-router-dom'
import { AUTH_PATH } from '~/app/lib/paths'
import { useUserStore } from '../user'
import { AuthApi } from './auth.api'
import { useAuthStore } from './auth.store'
import {
  ResetPasswordPayload,
  SelectTenantPayload,
  SignInPayload,
  SignUpPayload,
} from './auth.type'

export const AuthKeys = {
  SIGN_IN: 'sign-in',
  SELECT_TENANT: 'select-tenant',
  SIGN_UP: 'sign-up',
  FORGOT_PASSWORD: 'forgot-password',
  RESET_PASSWORD: 'reset-password',
  REFRESH_TOKEN: 'refresh-token',
}

export const useSignIn = () => {
  const navigate = useNavigate()
  const { setTempAuth } = useAuthStore()

  return useMutation({
    mutationKey: [AuthKeys.SIGN_IN],
    mutationFn: (data: SignInPayload) => AuthApi.signIn(data),
    onSuccess: (data) => {
      console.log('🔐 Sign in response:', {
        hasTenants: !!data.tenants,
        tenantsCount: data.tenants?.length || 0,
        tenants: data.tenants,
        hasTempToken: !!data.temp_token,
        hasToken: !!data.token,
      })

      if (data.tenants && data.tenants.length > 0) {
        // Always show tenant selection for better UX
        setTempAuth(data.temp_token || data.token, data.tenants)
        console.log('✅ Navigating to tenant selection')
        navigate(AUTH_PATH.TENANT_SELECTION)
      } else {
        console.log('⚠️ No tenants found in response - staying on login page')
      }
    },
  })
}

export const useSelectTenant = () => {
  const navigate = useNavigate()
  const { setAuth, setSelectedTenant, tenants } = useAuthStore()

  return useMutation({
    mutationKey: [AuthKeys.SELECT_TENANT],
    mutationFn: (data: SelectTenantPayload) => AuthApi.selectTenant(data),
    onSuccess: (data, variables) => {
      const accessTokenExpiry = jwtDecode(data.token)?.exp?.toString()
      setAuth(data.token, data.refresh_token, accessTokenExpiry)

      // Set the selected tenant
      const selectedTenant = tenants.find((t) => t.id === variables.tenant_id)
      if (selectedTenant) {
        setSelectedTenant(selectedTenant)
        console.log('🏢 Tenant selected:', selectedTenant.name)
      }

      message.success('Tenant selected successfully')

      // Add small delay to ensure auth state is properly set before navigation
      setTimeout(() => {
        navigate(AUTH_PATH.SELECT_WORKSPACE)
      }, 100)
    },
  })
}

export const useSignUp = () => {
  return useMutation({
    mutationKey: [AuthKeys.SIGN_UP],
    mutationFn: (data: SignUpPayload) => AuthApi.signUp(data),
    onSuccess: () => {},
  })
}

export const useForgotPassword = () => {
  return useMutation({
    mutationKey: [AuthKeys.FORGOT_PASSWORD],
    mutationFn: (email: string) => AuthApi.forgotPassword(email),
  })
}

export const useResetPassword = () => {
  return useMutation({
    mutationKey: [AuthKeys.RESET_PASSWORD],
    mutationFn: (data: ResetPasswordPayload) => AuthApi.resetPassword(data),
  })
}

const MAX_REFRESH_FAILURES = 3
const REFRESH_FAILURE_KEY = 'qa-genie-refresh-failures'
const REFRESH_FAILURE_RESET_TIME = 5 * 60 * 1000 // 5 minutes

let refreshPromise: Promise<any> | null = null

const getRefreshFailureCount = (): number => {
  try {
    const stored = localStorage.getItem(REFRESH_FAILURE_KEY)
    if (!stored) return 0

    const { count, timestamp } = JSON.parse(stored)
    const now = Date.now()

    // Reset count if more than 5 minutes have passed
    if (now - timestamp > REFRESH_FAILURE_RESET_TIME) {
      localStorage.removeItem(REFRESH_FAILURE_KEY)
      return 0
    }

    return count || 0
  } catch {
    return 0
  }
}

const incrementRefreshFailureCount = (): number => {
  const count = getRefreshFailureCount() + 1
  localStorage.setItem(
    REFRESH_FAILURE_KEY,
    JSON.stringify({
      count,
      timestamp: Date.now(),
    })
  )
  return count
}

const resetRefreshFailureCount = () => {
  localStorage.removeItem(REFRESH_FAILURE_KEY)
}

export const useRefreshToken = () => {
  const navigate = useNavigate()
  const { setAuth, setTempAuth } = useAuthStore()

  const currentUser = useUserStore((state) => state.currentUser)
  const logout = useUserStore((state) => state.logout)

  return useMutation({
    mutationKey: [AuthKeys.REFRESH_TOKEN],
    mutationFn: () => {
      if (refreshPromise) {
        return refreshPromise
      }

      const failureCount = getRefreshFailureCount()
      if (failureCount >= MAX_REFRESH_FAILURES) {
        return Promise.reject(new Error('Max refresh attempts exceeded'))
      }

      refreshPromise = AuthApi.refreshToken()
      return refreshPromise
    },
    onSuccess: async (data: any) => {
      resetRefreshFailureCount()

      // If refresh returns tenants, auto-select the stored tenant
      if (data.tenants && data.tenants.length > 0) {
        const { selectedTenant } = useAuthStore.getState()

        if (selectedTenant) {
          try {
            const tenantResponse = await AuthApi.selectTenant({
              tenant_id: selectedTenant.id,
              temp_token: data.temp_token || data.token,
            })

            const accessTokenExpiry = jwtDecode(tenantResponse.token)?.exp?.toString()
            setAuth(tenantResponse.token, tenantResponse.refresh_token, accessTokenExpiry)
          } catch (error) {
            logout()
            navigate(AUTH_PATH.SIGN_IN)
          }
        } else {
          setTempAuth(data.temp_token || data.token, data.tenants)
          navigate(AUTH_PATH.TENANT_SELECTION)
        }
      } else {
        const accessTokenExpiry = jwtDecode(data.token)?.exp?.toString()
        setAuth(data.token, data.refresh_token, accessTokenExpiry)

        if (!currentUser?.id) {
          window.location.reload()
        }
      }
    },
    onError: () => {
      refreshPromise = null
      const failureCount = incrementRefreshFailureCount()

      if (failureCount >= MAX_REFRESH_FAILURES) {
        logout()
        navigate(AUTH_PATH.SIGN_IN)
      }
    },
    onSettled: () => {
      refreshPromise = null
    },
  })
}

export const AuthHook = {
  useSignIn,
  useSelectTenant,
  useSignUp,
  useForgotPassword,
  useResetPassword,
  useRefreshToken,
}
